import { useEffect, useRef, useState } from 'react';
import {
  type Rgba,
  loadImageBitmap,
  removeColor,
  samplePixel,
  canvasToPngBlob,
} from './magicWand';

interface Props {
  file: File;
  onConfirm(result: { blob: Blob; width: number; height: number }): void;
  onCancel(): void;
}

/**
 * Lets the user tap a pixel to designate the background color, then
 * preview the resulting transparent PNG before confirming. Tolerance and
 * feather are exposed as sliders for fine-tuning.
 */
export function MagicWandPicker({ file, onConfirm, onCancel }: Props) {
  const [bitmap, setBitmap] = useState<ImageBitmap | null>(null);
  const [target, setTarget] = useState<Rgba | null>(null);
  const [tolerance, setTolerance] = useState(40);
  const [feather, setFeather] = useState(15);
  const [busy, setBusy] = useState(false);
  const previewRef = useRef<HTMLCanvasElement | null>(null);
  const sourceRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const bm = await loadImageBitmap(file);
        if (cancelled) {
          bm.close();
          return;
        }
        setBitmap(bm);
      } catch (err) {
        console.error('[magic-wand] failed to load image', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [file]);

  // Render initial source preview into the source canvas (unaltered).
  useEffect(() => {
    if (!bitmap || !sourceRef.current) return;
    const c = sourceRef.current;
    c.width = bitmap.width;
    c.height = bitmap.height;
    c.getContext('2d')?.drawImage(bitmap, 0, 0);
  }, [bitmap]);

  // Re-render preview when target/tolerance/feather change.
  useEffect(() => {
    if (!bitmap || !previewRef.current || !target) return;
    const out = removeColor(bitmap, target, { tolerance, feather });
    const ctx = previewRef.current.getContext('2d');
    if (!ctx) return;
    previewRef.current.width = out.width;
    previewRef.current.height = out.height;
    ctx.clearRect(0, 0, out.width, out.height);
    ctx.drawImage(out, 0, 0);
  }, [bitmap, target, tolerance, feather]);

  const handlePick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!bitmap) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ux = (e.clientX - rect.left) / rect.width;
    const uy = (e.clientY - rect.top) / rect.height;
    setTarget(samplePixel(bitmap, ux, uy));
  };

  const handleConfirm = async () => {
    if (!bitmap || busy) return;
    setBusy(true);
    try {
      const out = target ? removeColor(bitmap, target, { tolerance, feather }) : null;
      const canvas = out ?? (() => {
        const c = document.createElement('canvas');
        c.width = bitmap.width;
        c.height = bitmap.height;
        c.getContext('2d')!.drawImage(bitmap, 0, 0);
        return c;
      })();
      const blob = await canvasToPngBlob(canvas);
      onConfirm({ blob, width: bitmap.width, height: bitmap.height });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal
      onClick={(e) => {
        // Click outside the card dismisses.
        if (e.target === e.currentTarget) onCancel();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(6px)',
        zIndex: 60,
        display: 'grid',
        placeItems: 'center',
        padding: 16,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 720,
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
          background: 'rgba(11,11,15,0.96)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 16,
          padding: 20,
          display: 'grid',
          gap: 14,
          boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
        }}
      >
        <header style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Remove background</h2>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            style={{ background: 'transparent', border: 'none', color: 'var(--text-dim)', fontSize: 22, cursor: 'pointer' }}
          >
            ×
          </button>
        </header>
        <p style={{ color: 'var(--text-dim)', margin: 0, fontSize: 13 }}>
          Tap the source area to pick the color you want transparent (usually the center).
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 10,
            alignItems: 'start',
          }}
        >
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 4 }}>Source — tap to pick</div>
            <canvas
              ref={sourceRef}
              onClick={handlePick}
              style={{ width: '100%', height: 'auto', background: '#222', borderRadius: 8, cursor: 'crosshair' }}
            />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 4 }}>Preview</div>
            <canvas
              ref={previewRef}
              style={{
                width: '100%',
                height: 'auto',
                background:
                  'conic-gradient(#444 25%, transparent 0 50%, #444 0 75%, transparent 0) 0 0 / 16px 16px',
                borderRadius: 8,
              }}
            />
          </div>
        </div>
      {target && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                display: 'inline-block',
                width: 16,
                height: 16,
                borderRadius: 4,
                background: `rgb(${target.r}, ${target.g}, ${target.b})`,
                border: '1px solid #444',
              }}
            />
            <code style={{ fontSize: 12 }}>
              rgb({target.r}, {target.g}, {target.b})
            </code>
          </span>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            Tolerance
            <input
              type="range"
              min={0}
              max={120}
              value={tolerance}
              onChange={(e) => setTolerance(Number(e.target.value))}
            />
            <span style={{ minWidth: 28, textAlign: 'right' }}>{tolerance}</span>
          </label>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            Feather
            <input
              type="range"
              min={0}
              max={60}
              value={feather}
              onChange={(e) => setFeather(Number(e.target.value))}
            />
            <span style={{ minWidth: 28, textAlign: 'right' }}>{feather}</span>
          </label>
        </div>
      )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '10px 16px',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.18)',
              color: 'var(--text)',
              borderRadius: 999,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={busy}
            style={{
              padding: '10px 20px',
              background: '#ff3a8c',
              color: '#fff',
              border: 'none',
              borderRadius: 999,
              fontWeight: 700,
              cursor: 'pointer',
              opacity: busy ? 0.5 : 1,
            }}
          >
            {target ? 'Use this' : 'Keep as-is'}
          </button>
        </div>
      </div>
    </div>
  );
}
